/* eslint-disable max-lines */
import { treeUtil } from '@salescore/buff-common'
import type { ViewFieldsFragment } from '@salescore/client-api'
import { type Me, useMe } from '@salescore/client-common'
import { closeAnyLongTextViewer, useAppDispatch } from '@salescore/client-redux'
import {
  type CompiledFormViewConfig,
  type CompiledKpiPivotViewConfig,
  type CompiledKpiViewConfig,
  type CompiledSheetViewConfig,
  type CoreModel,
  makeIdentifiable,
  type NodePropertyName,
  NOT_FOUND_FIELD_NAME_PREFIX,
  POSTGRES_COLUMN_LENGTH_LIMIT,
  pruneSheetConfigTree,
  type ViewConfigField,
  type ViewConfigFilter,
  type ViewConfigFilterNode,
  type ViewConfigFilterNodeForJoinOn,
  type ViewConfigSheet,
  type ViewConfigSorter,
  type ViewConfigTreeNode,
  type ViewConfigWithMetric,
  type ViewQueryAggregation,
  type ViewQueryField,
  type ViewQueryMultiTablePivot,
  type ViewUiSheetColumn,
} from '@salescore/core'
import { Array, Option, pipe } from 'effect'
import { isNotNullish, isUndefined } from 'effect/Predicate'
import { useCallback, useMemo } from 'react'
import { atom, selector, useRecoilValue, useSetRecoilState } from 'recoil'

import { useVmid } from '../components/VmidContext'
import type { RSheetColumn, RSheetRecordNode } from '../domain/column'
import { bindViewConfigActions } from '../domain/viewConfig/viewConfigActions'
import { usePivotColumns } from '../hooks/usePivotColumns'
import { flatNodes } from '../state/nodeUtility'
import { kpiParameterAtom } from './navigation/atoms'
import { getCanEditField } from './view/functions/filterRelated'
import { isSameField, isSameNodeProperty } from './view/mutations/field/util'

const configAtomRaw = atom<ViewConfigWithMetric>({
  key: `view/configAtomRaw`,
  // この default 行は削除しない。値は RecoilRoot の initializeState がセットする前提だが、
  // 「どうせ initializeState で set されるから不要」と消すと、初期化タイミングが揃わないケースでビューが開けなくなる。
  // MR !6151 (issue #8781) で削除を試みたが問題が出て revert (commit ddb1197) された。
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  default: undefined as never,
})

export const configAtom = selector<ViewConfigWithMetric>({
  key: `view/configAtom`,
  get({ get }) {
    return get(configAtomRaw)
  },
  set({ set }, config) {
    set(configAtomRaw, config)
  },
})

export const useViewConfigMutation = (): ReturnType<typeof bindViewConfigActions> => {
  const setViewConfig = useSetRecoilState(configAtom)

  return useMemo(() => bindViewConfigActions(setViewConfig), [setViewConfig])
}

export const useMoveFieldMutation = () => {
  const viewConfig = useRecoilValue(configAtom)
  const { moveField } = useViewConfigMutation()
  return ({ oldFieldIndex, newFieldIndex }: { oldFieldIndex: number; newFieldIndex: number }): void => {
    moveField({ viewConfig, oldFieldIndex, newFieldIndex })
  }
}
export const useSetQueryField = () => {
  const setConfig = useSetRecoilState(configAtom)

  return ({
    config,
    field,
    override,
    onSetConfig,
  }: {
    config: ViewConfigSheet
    field: ViewQueryField
    override: Exclude<ViewConfigField['override'], undefined>['queryField']
    onSetConfig: () => void
  }): void => {
    const { fields } = config

    if (fields === undefined) {
      return
    }
    // eslint-disable-next-line sonarjs/todo-tag
    // oldValueはreadonlyになっているので、一旦jsonにして回避している // TODO: 他の解決策はあるか？
    const newFields = structuredClone(fields)
    const targetField = pipe(
      newFields,
      Array.findFirst((x): boolean => isSameField(x, field)),
      Option.getOrUndefined,
    )
    if (targetField === undefined) {
      return
    }
    targetField.override = {
      ...targetField.override,
      queryField: {
        ...targetField.override?.queryField,
        ...override,
      },
    }

    setConfig({
      ...config,
      fields: newFields,
    })
    onSetConfig()
  }
}
export const useSetRootNodeMutation = () => {
  const setConfig = useSetRecoilState(configAtom)

  return useCallback(
    ({ model, onSet }: { model: CoreModel; onSet: (node: ViewConfigTreeNode) => void }): void => {
      const newNode: ViewConfigTreeNode = {
        name: model.name,
        modelName: model.name,
        type: 'model',
      }
      setConfig({
        type: 'sheet' as const,
        tree: newNode,
      })
      onSet(newNode)
    },
    [setConfig],
  )
}

//
// eslint-disable-next-line sonarjs/todo-tag
// TODO: recoil-pagesとviewsでatoms,selectorが被っている
//

export const useSetNodeMutation = () => {
  const config = useViewConfigSheet()
  const setConfig = useSetRecoilState(configAtom)

  return ({ node, onSetConfig }: { node: ViewConfigTreeNode; onSetConfig: () => void }): void => {
    const { tree } = config
    if (tree === undefined) {
      // 初期化はsetRootNodeにより終わっているはず
      return
    }

    setConfig({
      ...config,
      tree: replaceTreeRec(tree, node),
    })
    onSetConfig()
  }
}

function replaceTreeRec(tree: ViewConfigTreeNode, node: ViewConfigTreeNode): ViewConfigTreeNode {
  if (tree.name === node.name) {
    return node
  }

  return {
    ...tree,
    children: tree.children?.map((child) => replaceTreeRec(child, node)),
  }
}
export const useViewConfigSheet = (): ViewConfigSheet => {
  const config = useRecoilValue(configAtom)
  return config.type === 'sheet' ? config : { type: 'sheet' }
}
export const useSetFieldDefaultValues = () => {
  const config = useViewConfigSheet()
  const setConfig = useSetRecoilState(configAtom)

  return ({ fieldsWithDefaultValue }: { fieldsWithDefaultValue: ViewConfigField[] }): void => {
    setConfig({
      ...config,
      fields: config.fields?.map((field) => {
        const fieldWithDefaultValue = fieldsWithDefaultValue.find((x) => isSameNodeProperty(x.property, field.property))
        if (fieldWithDefaultValue === undefined) {
          return field
        }
        return {
          ...field,
          override: {
            ...field.override,
            queryField: fieldWithDefaultValue.override?.queryField,
          },
        }
      }),
    })
  }
}
export const useRemoveField = () => {
  const setConfig = useSetRecoilState(configAtom)

  return ({
    config,
    me,
    field,
    onSetConfig,
  }: {
    config: ViewConfigSheet
    me: Me
    field: ViewQueryField
    onSetConfig: () => void
  }): void => {
    const canEditField = getCanEditField(me)
    if (!canEditField) {
      return // 本来辿り着かないはずなので、特にメッセージなどは出さない（出したいが、recoilの中でメッセージを表示させる機構が現状ない）
    }
    // eslint-disable-next-line sonarjs/todo-tag
    const isNotFoundField = field.name.startsWith(NOT_FOUND_FIELD_NAME_PREFIX) // TODO

    // eslint-disable-next-line sonarjs/todo-tag
    // TODO: このレイヤでnode,model,propertyのチェックは行うか？
    // const { streams } = get(connectionsSelector)
    // const { getNodeName } = get(querySelector)
    const configFields = config.fields ?? []
    const newConfig = {
      ...config,
      fields: isNotFoundField
        ? configFields.filter(
            (configField) =>
              makeIdentifiable(
                [NOT_FOUND_FIELD_NAME_PREFIX, configField.property.nodeName, configField.property.propertyName].join(
                  '_',
                ),
                POSTGRES_COLUMN_LENGTH_LIMIT,
              ) !== field.name,
          )
        : configFields.filter((configField) => !isSameField(configField, field)),
    }
    const prunedConfigTree = pruneSheetConfigTree(newConfig)

    setConfig({
      ...newConfig,
      tree: prunedConfigTree,
    })
    onSetConfig()
  }
}
export const useEditNodeMutation = () => {
  const config = useViewConfigSheet()
  const setConfig = useSetRecoilState(configAtom)

  return ({
    node,
    f,
    onSetConfig,
  }: {
    node: ViewConfigTreeNode
    f: (oldNode: ViewConfigTreeNode) => void // 破壊的操作
    onSetConfig: () => void
  }): void => {
    const { tree } = config
    if (tree === undefined) {
      // 初期化はsetRootNodeにより終わっているはず
      return
    }

    const newTree = treeUtil.replace(tree, node.name, f)

    setConfig({
      ...config,
      tree: newTree,
    })
    onSetConfig()
  }
}
export const useRemoveSorterMutation = () => {
  const config = useViewConfigSheet()
  const setConfig = useSetRecoilState(configAtom)
  return useCallback(
    ({ field, onSetConfig }: { field: ViewQueryField; onSetConfig: () => void }) => {
      const { sorters, fields } = config
      const targetField = fields?.find((x) => isSameField(x, field))
      if (targetField === undefined) {
        return
      }

      setConfig({
        ...config,
        sorters: (sorters ?? [])
          .filter((sorter) => !isSameNodeProperty(sorter.property, targetField.property))
          .filter(isNotNullish),
      })
      onSetConfig()
    },
    [config, setConfig],
  )
}
export const useRemoveFilterByField = () => {
  const config = useViewConfigSheet()
  const setConfig = useSetRecoilState(configAtom)

  return ({ field, onSetConfig }: { field: ViewQueryField; onSetConfig: () => void }): void => {
    const { filterTree } = config
    if (filterTree === undefined) {
      return
    }
    const configField = (config.fields ?? []).find((configField) => isSameField(configField, field))
    if (configField === undefined) {
      return
    }
    setConfig({
      ...config,
      filterTree: removeLeafsByFilterRec(filterTree, configField),
    })
    onSetConfig()
  }
}

function removeLeafsByFilterRec(node: ViewConfigFilterNode, field: ViewConfigField): ViewConfigFilterNode {
  return {
    ...node,
    children: node.children.map((child) => removeLeafsByFilterRec(child, field)),
    leafs: node.leafs.filter((leaf) => leaf.type === 'property' && !isSameNodeProperty(leaf.property, field.property)),
  }
}

export const useSetFieldMutation = () => {
  const me = useMe()
  const config = useViewConfigSheet()
  const setConfig = useSetRecoilState(configAtom)
  const canEditField = getCanEditField(me)
  if (!canEditField) {
    // 本来辿り着かないはずなので、特にメッセージなどは出さない（出したいが、recoilの中でメッセージを表示させる機構が現状ない）
    return (): void => {
      /* empty */
    }
  }
  const fields = config.fields ?? []

  return ({ field, onSetConfig }: { field: ViewConfigField; onSetConfig: () => void }): void => {
    const targetIndex = fields.findIndex((x) => isSameNodeProperty(x.property, field.property))
    if (targetIndex !== -1) {
      setConfig({
        ...config,
        fields: [...fields.slice(0, targetIndex), field, ...fields.slice(targetIndex + 1)],
      })
      onSetConfig()
    }
  }
}
//
// 別のシートのfieldsを、現在のviewのnodeにインポートする
// eslint-disable-next-line sonarjs/todo-tag
// 引数のfieldsは、streamがnodeと等しいことを前提としている（TODO: このvalidationをUIレイヤでしかやっていないので、このレイヤでも行いたい）
//
export const useAddFieldsByFieldsMutation = () => {
  const me = useMe()
  const config = useViewConfigSheet()
  const setConfig = useSetRecoilState(configAtom)

  return useCallback(
    ({
      fields,
      targetField,
      onSetConfig,
    }: {
      fields: ViewConfigField[]
      targetField: ViewConfigField
      onSetConfig: () => void
    }): void => {
      const canEditField = getCanEditField(me)
      if (!canEditField) {
        return // 本来辿り着かないはずなので、特にメッセージなどは出さない（出したいが、recoilの中でメッセージを表示させる機構が現状ない）
      }
      const currentFields = config.fields ?? []
      const targetIndex = currentFields.findIndex((field) => isSameNodeProperty(field.property, targetField.property))

      const newFields = fields
        .map((field): ViewConfigField => ({
          type: 'property',
          property: {
            nodeName: targetField.property.nodeName,
            modelName: field.property.modelName,
            propertyName: field.property.propertyName,
          },
          override: field.override,
        }))
        .filter(isNotNullish)
        .filter((field) => currentFields.every((x) => !isSameNodeProperty(x.property, field.property)))
      setConfig({
        ...config,
        // taregetIndex = -1 の処理は特に考えられてないが大丈夫なのか？
        fields: [...currentFields.slice(0, targetIndex + 1), ...newFields, ...currentFields.slice(targetIndex + 1)],
      })
      onSetConfig()
    },
    [config, me, setConfig],
  )
}
export const useSetFilterMutation = () => {
  const config = useViewConfigSheet()
  const setConfig = useSetRecoilState(configAtom)

  return ({ filterTree }: { filterTree: ViewConfigFilterNode | undefined }): void => {
    setConfig({
      ...config,
      filterTree: filterTree ?? {
        logicalOperator: 'and',
        leafs: [],
        children: [],
      },
    })
  }
}
export const useSetSearchableFields = () => {
  const config = useViewConfigSheet()
  const setConfig = useSetRecoilState(configAtom)

  return ({ searchableFields, onSetConfig }: { searchableFields: ViewQueryField[]; onSetConfig: () => void }): void => {
    if (config.fields === undefined) {
      return
    }

    const newFields = config.fields.map((field: ViewConfigField): ViewConfigField => ({
      ...field,
      override: {
        ...field.override,
        queryField: {
          ...field.override?.queryField,
          meta: {
            ...field.override?.queryField?.meta,
            // eslint-disable-next-line sonarjs/todo-tag
            // TODO: fieldTypeがstringかどうかの判定はcompielのロジックで期待
            searchable: searchableFields.some((x) => isSameField(field, x)),
          },
        },
      },
    }))
    setConfig({
      ...config,
      fields: newFields,
    })
    onSetConfig()
  }
}

type UiColumn = Partial<ViewUiSheetColumn>
export const useSetUiColumn = () => {
  const config = useViewConfigSheet()
  const setConfig = useSetRecoilState(configAtom)

  const { fields } = config

  return ({
    field,
    column,
    onSetConfig,
  }: {
    field: ViewQueryField
    column: UiColumn
    onSetConfig: () => void
  }): void => {
    if (fields === undefined) {
      return
    }
    // eslint-disable-next-line sonarjs/todo-tag
    // oldValueはreadonlyになっているので、一旦jsonにして回避している // TODO: 他の解決策はあるか？
    const newFields = structuredClone(fields)
    const fieldIndex = newFields.findIndex((x) => isSameField(x, field))
    if (fieldIndex === -1) {
      return
    }
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    newFields[fieldIndex]!.override = {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      ...newFields[fieldIndex]!.override,
      uiColumn: {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        ...newFields[fieldIndex]!.override?.uiColumn,
        ...column,
      },
    }

    setConfig({
      ...config,
      fields: newFields,
    })
    onSetConfig()
  }
}
export type Side = 'left' | 'right'

export const useUpsertSorter = () => {
  const config = useViewConfigSheet()
  const setConfig = useSetRecoilState(configAtom)
  const { sorters, fields } = config

  return ({
    field,
    order,
    sortBy,
  }: {
    field: ViewQueryField
    order: 'asc' | 'desc'
    sortBy: 'value' | 'label'
  }): void => {
    setConfig({
      ...config,
      sorters: upsertSorter({ currentSorters: sorters ?? [], fields, field, order, sortBy }),
    })
  }
}
export const upsertSorter = ({
  currentSorters,
  fields,
  field,
  order,
  sortBy,
}: {
  currentSorters: ViewConfigSorter[]
  fields: ViewConfigSheet['fields'] | undefined
  field: ViewQueryField
  order: 'asc' | 'desc'
  sortBy: 'value' | 'label'
}): ViewConfigSorter[] => {
  if (isUndefined(fields)) {
    return currentSorters
  }

  const newSortField = fields.find((x) => isSameField(x, field))
  if (isUndefined(newSortField)) {
    return currentSorters
  }

  const requestedSorter = createSorter(newSortField.property, order, sortBy)

  return createNewSorters({
    currentSorters,
    requestedSorter,
  })
}

function createNewSorters({
  currentSorters,
  requestedSorter,
}: {
  currentSorters: ViewConfigSorter[]
  requestedSorter: ViewConfigSorter
}): ViewConfigSorter[] {
  const existingSorterIndex = currentSorters.findIndex((sorter) =>
    isSameNodeProperty(sorter.property, requestedSorter.property),
  )

  const newSorters = currentSorters.filter((sorter) => !isSameNodeProperty(sorter.property, requestedSorter.property))
  if (existingSorterIndex !== -1) {
    return newSorters.toSpliced(existingSorterIndex, 0, requestedSorter)
  }

  return [...newSorters, requestedSorter]
}

function createSorter(property: NodePropertyName, order: 'asc' | 'desc', sortBy: 'value' | 'label'): ViewConfigSorter {
  return {
    type: 'property',
    property,
    order,
    sortBy,
  }
}
export const useResizeColumnMutation = (
  // eslint-disable-next-line sonarjs/use-type-alias
  compiledConfig: CompiledSheetViewConfig | CompiledKpiViewConfig | CompiledKpiPivotViewConfig | CompiledFormViewConfig,
  view: ViewFieldsFragment,
) => {
  const config = useRecoilValue(configAtom)
  const { columnKeys, rowColumns, systemColumns } = usePivotColumns(compiledConfig, view)
  const parameter = useRecoilValue(kpiParameterAtom)
  const setUiColumn = useSetUiColumn()
  const setConfig = useSetRecoilState(configAtom)
  const vmid = useVmid()
  const dispatch = useAppDispatch()

  return ({
    width,
    column,
    allColumnIndex,
    setHasChangeToViewSchema,
  }: {
    width: number
    column: RSheetColumn<RSheetRecordNode>
    allColumnIndex: number
    setHasChangeToViewSchema: (hasChangeToViewSchema: boolean) => void
    // eslint-disable-next-line complexity,sonarjs/cognitive-complexity
  }): void => {
    // 列幅変更時は開いている長文 viewer の Popover を閉じる（幅が変わると表示位置/サイズが破綻するため）
    dispatch(closeAnyLongTextViewer({ vmid }))
    if (config.type === 'sheet') {
      if (column.field === undefined) {
        // システムカラム等、 config.field が存在しない場合は何もしない。つまり列幅の変更を禁じる。
        return
      }
      setUiColumn({
        field: column.field,
        column: { width },
        onSetConfig: () => {
          setHasChangeToViewSchema(true)
        },
      })
    }
    if (config.type === 'kpiPivot' || config.type === 'kpi') {
      const type =
        allColumnIndex < systemColumns.length
          ? undefined
          : // eslint-disable-next-line sonarjs/no-nested-conditional
            allColumnIndex < systemColumns.length + rowColumns.length
            ? 'row'
            : 'column'
      if (type === 'row') {
        // 行のwidthを設定
        const index = allColumnIndex - systemColumns.length
        const dimension = parameter.pivot.rows[index]
        if (dimension === undefined) {
          return
        }
        // 型推論の都合で場合わけが必要（ロジックは同じ）
        if (config.type === 'kpi') {
          if (config.ui === undefined) {
            return // ありえないはず
          }
          setConfig({
            ...config,
            ui: {
              ...config.ui,
              appearance: {
                ...config.ui.appearance,
                rows: Array.dedupeWith(
                  [
                    { key: dimension.key, width },
                    ...(config.ui.appearance?.rows ?? []).filter((x) => x.key !== dimension.key),
                  ],
                  (a, b) => a.key === b.key,
                ),
              },
            },
          })
          setHasChangeToViewSchema(true)
        } else {
          setConfig({
            ...config,
            ui: {
              ...config.ui,
              appearance: {
                ...config.ui?.appearance,
                rows: Array.dedupeWith(
                  [
                    { key: dimension.key, width },
                    ...(config.ui?.appearance?.rows ?? []).filter((x) => x.key !== dimension.key),
                  ],
                  (a, b) => a.key === b.key,
                ),
              },
            },
          })
          setHasChangeToViewSchema(true)
        }
      } else if (type === 'column') {
        // 列のキーにwidhtを設定
        const index = allColumnIndex - systemColumns.length - rowColumns.length
        const dimension = parameter.pivot.columns.at(-1)
        const columnKey = columnKeys[index]
        if (dimension === undefined || columnKey === undefined) {
          return
        }
        const value = columnKey.at(-1)?.value
        if (value === undefined) {
          return
        }
        const previous = (config.ui?.appearance?.columns ?? []).find((x) => x.key === dimension.key)
        if (config.type === 'kpi') {
          if (config.ui === undefined) {
            return // ありえないはず
          }
          setConfig({
            ...config,
            ui: {
              ...config.ui,
              appearance: {
                ...config.ui.appearance,
                columns: Array.dedupeWith(
                  [
                    {
                      key: dimension.key,
                      keys: Array.dedupeWith(
                        [{ value, width }, ...(previous?.keys ?? []).filter((x) => x.value !== value)],
                        (a, b) => a.value === b.value,
                      ),
                    },
                    ...(config.ui.appearance?.columns ?? []).filter((x) => x.key !== dimension.key),
                  ],
                  (a, b) => a.key === b.key,
                ),
              },
            },
          })
          setHasChangeToViewSchema(true)
        } else {
          setConfig({
            ...config,
            ui: {
              ...config.ui,
              appearance: {
                ...config.ui?.appearance,
                columns: Array.dedupeWith(
                  [
                    {
                      key: dimension.key,
                      keys: Array.dedupeWith(
                        [{ value, width }, ...(previous?.keys ?? []).filter((x) => x.value !== value)],
                        (a, b) => a.value === b.value,
                      ),
                    },
                    ...(config.ui?.appearance?.columns ?? []).filter((x) => x.key !== dimension.key),
                  ],
                  (a, b) => a.key === b.key,
                ),
              },
            },
          })
          setHasChangeToViewSchema(true)
        }
      }
    }
  }
}

export const useAggregationQueries = (
  compiledConfig: CompiledSheetViewConfig | CompiledKpiViewConfig | CompiledKpiPivotViewConfig | CompiledFormViewConfig,
): ViewQueryAggregation[] =>
  useMemo(() => compiledConfig.queries.filter((x) => x.type === 'aggregation'), [compiledConfig.queries])

export const useMultiTablePivotQuery = (
  compiledConfig: CompiledSheetViewConfig | CompiledKpiViewConfig | CompiledKpiPivotViewConfig | CompiledFormViewConfig,
): ViewQueryMultiTablePivot | undefined =>
  useMemo(() => compiledConfig.queries.find((x) => x.type === 'multiTablePivot'), [compiledConfig.queries])

// 組織とシート固有の AJ 設定を混ぜたもの。これを UI で使うようにする。
export const resolveShouldJoinRecordsInApplication = (me: Me, config: ViewConfigSheet): boolean =>
  config.meta?.shouldJoinRecordsInApplication ?? me.organization.setting.shouldJoinRecordsInApplication

export const useFlattenFilterNodeLeafs = ({ filterTree }: ViewConfigSheet): ViewConfigFilter[] =>
  useMemo(() => (filterTree === undefined ? [] : flatNodes(filterTree).flatMap((x) => x.leafs)), [filterTree])

export const useGetConfigNode = ({ tree }: ViewConfigSheet): ((nodeName: string) => ViewConfigTreeNode | undefined) => {
  const flattenNodes = useMemo(() => flatNodes(tree), [tree])
  return useCallback(
    (nodeName: string): ViewConfigTreeNode | undefined => flattenNodes.find((x) => x.name === nodeName),
    [flattenNodes],
  )
}

export function pickAdditionalJoinOns(tree: ViewConfigTreeNode) {
  const filterNodeForJoinOns: ViewConfigFilterNodeForJoinOn[] = []

  function rec(tree: ViewConfigTreeNode) {
    if (tree.additionalJoinOn !== undefined) {
      filterNodeForJoinOns.push(tree.additionalJoinOn)
    }
    tree.children?.forEach((child) => {
      rec(child)
    })
  }

  rec(tree)
  return filterNodeForJoinOns
}
